import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Trash2, AlertTriangle, AlertOctagon } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only viewer for client-side warn/error events broadcast from
 * frontend `logger.warn` / `logger.error` calls. Reads from GET
 * /api/admin/client-logs, filterable by level. Provides a "Clear all"
 * action (destructive, confirm-guarded) that wipes the collection.
 */
export default function AdminClientLogs() {
  const [logs, setLogs] = useState([]);
  const [level, setLevel] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = level === "all" ? {} : { level };
      const { data } = await api.get("/admin/client-logs", { params });
      setLogs(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load client logs");
    } finally {
      setLoading(false);
    }
  }, [level]);

  useEffect(() => { load(); }, [load]);

  const clearAll = async () => {
    if (!window.confirm("Delete all client log entries? This cannot be undone.")) return;
    try {
      const { data } = await api.delete("/admin/client-logs");
      toast.success(`Cleared ${data.deleted} log entries`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clear logs");
    }
  };

  return (
    <div className="mt-12" data-testid="admin-client-logs-panel">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="vq-overline mb-2">Observability</div>
          <h2 className="font-display font-bold text-2xl text-slate-900">Client-side event log</h2>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Warnings and errors broadcast in real time from every user&apos;s browser via <span className="font-mono text-xs">logger.warn</span> / <span className="font-mono text-xs">logger.error</span>. Throttled to one entry per unique message per 30 seconds.
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger data-testid="admin-client-logs-level" className="w-40 h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All levels</SelectItem>
              <SelectItem value="warn">Warnings only</SelectItem>
              <SelectItem value="error">Errors only</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-client-logs-refresh"
                  className="h-11 border-slate-300 font-semibold">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button variant="outline" onClick={clearAll} data-testid="admin-client-logs-clear"
                  className="h-11 border-slate-300 font-semibold text-red-700 hover:text-red-800 hover:border-red-300">
            <Trash2 className="w-4 h-4 mr-2" /> Clear
          </Button>
        </div>
      </div>

      <div className="vq-card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-slate-500 w-24">Level</th>
              <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-slate-500 w-48">When</th>
              <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-slate-500 w-40">Who</th>
              <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-slate-500">Message</th>
            </tr>
          </thead>
          <tbody data-testid="admin-client-logs-tbody">
            {logs.map((row) => (
              <tr key={row.log_id} className="border-b border-slate-100">
                <td className="px-4 py-3">
                  {row.level === "error"
                    ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[10px] bg-rose-50 text-rose-700 border border-rose-200"><AlertOctagon className="w-3 h-3" /> ERROR</span>
                    : <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[10px] bg-amber-50 text-amber-700 border border-amber-200"><AlertTriangle className="w-3 h-3" /> WARN</span>}
                </td>
                <td className="px-4 py-3 text-xs font-mono text-slate-600">
                  {new Date(row.created_at).toLocaleString()}
                </td>
                <td className="px-4 py-3 text-xs font-mono text-slate-700">
                  {row.user?.email || <span className="text-slate-400">anonymous</span>}
                </td>
                <td className="px-4 py-3 text-xs font-mono text-slate-800 max-w-2xl">
                  <div className="whitespace-pre-wrap break-words">{row.message}</div>
                  {row.url && (
                    <div className="text-slate-400 mt-1 truncate">
                      {row.url.replace(window.location.origin, "")}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {logs.length === 0 && (
              <tr><td colSpan={4} className="text-center py-8 text-sm text-slate-500">
                No client-side events recorded. Everything is quiet — that&apos;s a good thing.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
